import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AlertTriangle, X } from "lucide-react";

type Check = { id: string; name: string; ok: boolean | "warn"; detail: string; fix?: string };

const DISMISS_KEY = "webhook-health-banner-dismissed-at";
const DISMISS_TTL_MS = 30 * 60 * 1000; // 30 minutes

export function WebhookHealthBanner() {
  const [failed, setFailed] = useState<Check[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (ts && Date.now() - ts < DISMISS_TTL_MS) {
      setDismissed(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("stripe-webhook-checklist");
        if (cancelled || !data?.checks) return;
        setFailed((data.checks as Check[]).filter((c) => c.ok === false));
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (dismissed || failed.length === 0) return null;

  return (
    <div className="mb-4 border border-rose-500/50 bg-rose-500/10 text-rose-100 px-4 py-3 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-rose-300" />
      <div className="flex-1 text-sm">
        <div className="font-display tracking-widest text-xs uppercase text-rose-200">
          Stripe webhook checklist: {failed.length} failing check{failed.length === 1 ? "" : "s"}
        </div>
        <ul className="mt-1 list-disc pl-5 text-xs space-y-0.5">
          {failed.slice(0, 3).map((c) => (
            <li key={c.id}><span className="font-mono">{c.name}</span> — {c.detail}</li>
          ))}
        </ul>
        <Link
          to="/admin/stripe/webhook-checklist"
          className="inline-block mt-2 text-xs font-mono uppercase tracking-widest underline text-rose-50"
        >
          Investigate →
        </Link>
      </div>
      <button
        onClick={() => {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
          setDismissed(true);
        }}
        aria-label="Dismiss"
        className="text-rose-200 hover:text-rose-50"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}